/* SaGó — D6 · chevrones para pasar de foto sin bajar a la galería.
 *
 * La mitad del trabajo la hace el CSS: Dawn ya renderiza un `.slider-buttons`
 * con prev/next dentro del <slider-component> de la galería, y en móvil
 * funcionan solos porque ahí la lista de medios SÍ es un carrusel. Lo único que
 * les pasaba es que estaban debajo de la foto en vez de encima.
 *
 * En escritorio no vale lo mismo, y conviene saber por qué antes de tocar:
 * con `gallery_layout: thumbnail_slider`, Dawn esconde esos botones
 * (section-main-product.css, `.product__media-container .slider-buttons`) y la
 * lista deja de ser un carrusel — sólo se pinta el medio activo y se navega por
 * las miniaturas. Un botón de carrusel ahí no movería nada.
 *
 * Así que en escritorio se reutiliza la API que Dawn ya expone en el elemento
 * <media-gallery>: setActiveMedia(mediaId, prepend). No se bifurca ningún
 * fichero de Dawn — ni el snippet ni media-gallery.js — para que la próxima
 * actualización no tenga que fusionar nada. Ver evidencia/bloque-C-D.md.
 *
 * Medido en la preview: la galería de producto expone setActiveMedia y
 * data-desktop-layout="thumbnail_slider" con 4 medios.
 */
(() => {
  const ESCRITORIO = window.matchMedia('(min-width: 750px)');

  /* Sólo los <li> de la galería, y NO cualquier [data-media-id].
     product-thumbnail.liquid pone data-media-id="{{ media.id }}" —el id pelado,
     sin el prefijo de sección— también en el <button> de zoom que va DENTRO de
     cada <li>, y en <product-model>/<deferred-media> para vídeo y 3D. Con un
     selector abierto, una galería de 4 fotos devolvía 8 elementos intercalados:
     el «siguiente» era el botón de la misma foto, no la foto siguiente. Y como
     en escritorio Dawn esconde todo .product__media-item:not(.is-active), pasar
     el is-active a un botón dejaba la galería EN BLANCO. */
  const mediosDe = (galeria) => [
    ...galeria.querySelectorAll('[id^="GalleryViewer"] .product__media-item[data-media-id]'),
  ];

  function mover(galeria, paso) {
    const medios = mediosDe(galeria);
    if (medios.length < 2) return;
    const actual = medios.findIndex((m) => m.classList.contains('is-active'));
    const destino = medios[((actual < 0 ? 0 : actual) + paso + medios.length) % medios.length];
    if (destino) galeria.setActiveMedia(destino.dataset.mediaId, false);
  }

  /* En captura, para llegar antes que el manejador del <slider-component> de
     Dawn. En móvil no se hace nada: ahí ese manejador es el que vale, y
     duplicarlo saltaría dos fotos de una. */
  document.addEventListener(
    'click',
    (ev) => {
      if (!ESCRITORIO.matches) return;
      const boton = ev.target.closest('.product__media-wrapper .slider-button');
      if (!boton) return;
      const galeria = boton.closest('media-gallery');
      if (!galeria || typeof galeria.setActiveMedia !== 'function') return;
      ev.preventDefault();
      ev.stopPropagation();
      mover(galeria, boton.name === 'previous' ? -1 : 1);
    },
    true
  );

  /* 24/09 · los chevrones, sobre la foto y no en los bordes de la columna.
     Desde «Antes de publicar» la foto de escritorio se estrecha a su proporción
     para no recortarse (sago-overrides.css, bloque F3): una 2:3 mide 487 de los
     737 de la columna a 1440. La capa de los botones cubre el visor entero, así
     que se quedaban a ~125px de la foto, en el blanco. Se le pasa al CSS el hueco
     que deja la foto ACTIVA a cada lado (`--sago-flechas-izq/-der`): cada foto
     puede tener otra proporción, y en escritorio sólo se ve la activa. En móvil
     la foto ocupa el ancho y el visor es un carrusel: ahí no se toca. */
  function ajustarFlechas(visor) {
    if (!ESCRITORIO.matches) {
      visor.style.removeProperty('--sago-flechas-izq');
      visor.style.removeProperty('--sago-flechas-der');
      return;
    }
    const foto = visor.querySelector('.product__media-item.is-active .product-media-container')
      || visor.querySelector('.product__media-item .product-media-container');
    if (!foto) return;
    const v = visor.getBoundingClientRect();
    const f = foto.getBoundingClientRect();
    if (!f.width) return;
    visor.style.setProperty('--sago-flechas-izq', `${Math.max(0, Math.round(f.left - v.left))}px`);
    visor.style.setProperty('--sago-flechas-der', `${Math.max(0, Math.round(v.right - f.right))}px`);
  }

  // Idempotente por visor: el editor repinta la sección y el DOMContentLoaded
  // no vuelve a sonar (shopify:section:load).
  function vigilarVisores(raiz = document) {
    raiz.querySelectorAll('slider-component[id^="GalleryViewer"]').forEach((visor) => {
      ajustarFlechas(visor);
      if (visor.dataset.sagoFlechas) return;
      visor.dataset.sagoFlechas = '1';
      // Cambiar de foto (flechas, miniaturas o variante) mueve el is-active.
      new MutationObserver(() => ajustarFlechas(visor)).observe(visor, { subtree: true, attributes: true, attributeFilter: ['class'] });
    });
  }

  const todos = () => document.querySelectorAll('slider-component[id^="GalleryViewer"]').forEach(ajustarFlechas);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => vigilarVisores());
  else vigilarVisores();
  document.addEventListener('shopify:section:load', (ev) => vigilarVisores(ev.target));
  window.addEventListener('resize', todos);
  ESCRITORIO.addEventListener('change', todos);
})();
